import React from "react";
import {
  graphql,
  HeadProps,
  useStaticQuery,
  withAssetPrefix,
} from "gatsby";
import { PageContextIntl } from "@src/components/general/WrapPageElement/PageContext";
import {
  GATSBY_SOURCE_SUSHISHOP_BASE_URL,
  GATSBY_CONF_SHOP_NAME,
} from "@src/utils/constants";
import JsonLdScript from "design-system/src/Utils/JsonLdScript";
// import SuspenseAfterInteraction from "../AfterInteraction/SuspenseAfterInteraction";
// import SuspenseAfterSetup from "../AfterSetup/SuspenseAfterSetup";

export type DefaultHeadProps<
  DataType = object,
  PageContextType = PageContextIntl
> = HeadProps<DataType, PageContextType> & {
  children?: React.ReactNode;
  title?: string;
  description?: string;
  noindex?: boolean;
};

const BASE_URL = GATSBY_SOURCE_SUSHISHOP_BASE_URL;
const APPLE_ITUNES_APP = process.env.GATSBY_APPLE_ITUNES_APP || "";

const GetSeoConfigurations = (language: string) => {
  const data = useStaticQuery<Queries.AllSeoConfigurationsQuery>(graphql`
    query AllSeoConfigurations {
      _META_TITLE_: allConfiguration(filter: { name: { eq: "_META_TITLE_" } }) {
        nodes {
          value
          lang
        }
      }
      _META_DESCRIPTION_: allConfiguration(
        filter: { name: { eq: "_META_DESCRIPTION_" } }
      ) {
        nodes {
          value
          lang
        }
      }
      _GOOGLE_SITE_VERIF_: allConfiguration(
        filter: { name: { eq: "_GOOGLE_SITE_VERIF_" } }
      ) {
        nodes {
          value
          lang
        }
      }
      _FACEBOOK_URL_: allConfiguration(
        filter: { name: { eq: "_FACEBOOK_URL_" } }
      ) {
        nodes {
          value
          lang
        }
      }
      _INSTAGRAM_URL_: allConfiguration(
        filter: { name: { eq: "_INSTAGRAM_URL_" } }
      ) {
        nodes {
          value
          lang
        }
      }
      _TWITTER_URL_: allConfiguration(
        filter: { name: { eq: "_TWITTER_URL_" } }
      ) {
        nodes {
          value
          lang
        }
      }
      _YOUTUBE_URL_: allConfiguration(
        filter: { name: { eq: "_YOUTUBE_URL_" } }
      ) {
        nodes {
          value
          lang
        }
      }
      _LINKEDIN_URL_: allConfiguration(
        filter: { name: { eq: "_LINKEDIN_URL_" } }
      ) {
        nodes {
          value
          lang
        }
      }
      _PINTEREST_URL_: allConfiguration(
        filter: { name: { eq: "_PINTEREST_URL_" } }
      ) {
        nodes {
          value
          lang
        }
      }
    }
  `);
  const getLangValue = (
    {
      nodes,
    }: {
      readonly nodes: readonly {
        value?: string | null;
        lang?: string | null;
      }[];
    },
    defaultValue: string
  ) => nodes.find((node: any) => node.lang === language)?.value || defaultValue;
  const _META_TITLE_ = getLangValue(data._META_TITLE_, "Sushi Shop");
  const _META_DESCRIPTION_ = getLangValue(data._META_DESCRIPTION_, "");
  const _GOOGLE_SITE_VERIF_ = getLangValue(data._GOOGLE_SITE_VERIF_, "");
  const _FACEBOOK_URL_ = getLangValue(data._FACEBOOK_URL_, "");
  const _INSTAGRAM_URL_ = getLangValue(data._INSTAGRAM_URL_, "");
  const _TWITTER_URL_ = getLangValue(data._TWITTER_URL_, "");
  const _YOUTUBE_URL_ = getLangValue(data._YOUTUBE_URL_, "");
  const _LINKEDIN_URL_ = getLangValue(data._LINKEDIN_URL_, "");
  const _PINTEREST_URL_ = getLangValue(data._PINTEREST_URL_, "");
  return {
    _META_TITLE_,
    _META_DESCRIPTION_,
    _GOOGLE_SITE_VERIF_,
    _FACEBOOK_URL_,
    _INSTAGRAM_URL_,
    _TWITTER_URL_,
    _YOUTUBE_URL_,
    _LINKEDIN_URL_,
    _PINTEREST_URL_,
  };
};
const DefaultHead = (headProps: DefaultHeadProps) => {
  const {
    pageContext,
    children,
    title = "",
    description = "",
    noindex = false,
  } = headProps;
  const { intl, meta_title: contextMetaTitle = "" } = pageContext;
  const { language, alternates = [] } = intl;
  const {
    _META_TITLE_,
    _META_DESCRIPTION_,
    _GOOGLE_SITE_VERIF_,
    _FACEBOOK_URL_,
    _INSTAGRAM_URL_,
    _TWITTER_URL_,
    _YOUTUBE_URL_,
    _LINKEDIN_URL_,
    _PINTEREST_URL_,
  } = GetSeoConfigurations(language);

  let meta_title = title || contextMetaTitle || _META_TITLE_;
  let meta_description = description || _META_DESCRIPTION_;
  const FAVICON_BASE_URL = `${BASE_URL}/static/favicons/`;
  const appleTouchIcons = [
    "57x57",
    "60x60",
    "72x72",
    "76x76",
    "114x114",
    "120x120",
    "144x144",
    "152x152",
    "180x180",
  ].map((size) => ({
    sizes: size,
    rel: "apple-touch-icon",
    href: `${FAVICON_BASE_URL}apple-touch-icon-${size}.png`,
  }));
  const favicons = ["16x16", "32x32", "96x96", "192x192", "favicon.ico"].map(
    (size) => {
      let rel = "icon";
      let icon_filename = `favicon-${size}.png`;
      let type = "image/png";
      if (size === "favicon.ico") {
        rel = "shortcut icon";
        icon_filename = "favicon.ico";
        type = "image/x-icon";
      } else if (size === "192x192") {
        icon_filename = "android-chrome-192x192.png";
      }

      const href = `${FAVICON_BASE_URL}${icon_filename}`;
      return {
        sizes: size,
        rel,
        href,
        type,
      };
    }
  );
  const icons = [...favicons, ...appleTouchIcons];

  const sameAs = [
    _FACEBOOK_URL_,
    _INSTAGRAM_URL_,
    _TWITTER_URL_,
    _YOUTUBE_URL_,
    _LINKEDIN_URL_,
    _PINTEREST_URL_,
  ].filter(Boolean);

  return (
    <>
      <JsonLdScript
        type="WebSite"
        rawDatas={{
          name: GATSBY_CONF_SHOP_NAME,
          url: BASE_URL,
          publisher: {
            "@type": "Organization",
            name: GATSBY_CONF_SHOP_NAME,
            logo: {
              "@type": "ImageObject",
              url: withAssetPrefix("/medias/seo/logo-hero.jpg"),
              width: 600,
              height: 60,
            },
            ...(sameAs.length > 0 && { sameAs }),
          },
        }}
      />
      <html lang={language} />
      <title key={"default_meta_title"}>{meta_title}</title>
      {noindex && (
        <meta key="robots" name="robots" content="noindex, nofollow" />
      )}
      <meta key="og:title" property="og:title" content={meta_title} />
      <meta key="description" name="description" content={meta_description} />
      <meta
        key="og:description"
        name="og:description"
        content={meta_description}
      />
      <div dangerouslySetInnerHTML={{ __html: _GOOGLE_SITE_VERIF_ }}></div>

      {alternates?.map(({ lang, uri }, key) => (
        <link
          key={key}
          href={BASE_URL + uri}
          {...(lang === language
            ? { rel: "canonical" }
            : { rel: "alternate", hrefLang: lang })}
        />
      ))}
      {APPLE_ITUNES_APP && (
        <meta
          key="apple-itunes-app"
          name="apple-itunes-app"
          content={APPLE_ITUNES_APP}
        />
      )}
      {icons.map((linkProps, key) => (
        <link {...{ key }} {...linkProps} />
      ))}
      {children}
    </>
  );
};

export default DefaultHead;
